/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 04 2025
 **/

import React, { PropsWithChildren, Ref, forwardRef, useImperativeHandle, useRef } from "react";

import { ZIndex } from "@/frameworks/core/commons";

import useMinimizeDraggable from "./useMinimizeDraggable";

export interface DraggableHandle {
	reset: (pos: { x: number; y: number }) => void;
}

const MinimizeDraggable = <T,>(props: PropsWithChildren<{ minimize: boolean }> & T, ref: Ref<DraggableHandle>) => {
	const divRef = useRef<HTMLDivElement>(null);
	const { listeners, coordinate, reset } = useMinimizeDraggable();

	useImperativeHandle(ref, () => ({
		reset: (position: { x: number; y: number }) => {
			reset(position);
			if (divRef.current) {
				divRef.current.style.top = `${position.y}px`;
				divRef.current.style.left = `${position.x}px`;
				divRef.current.style.cursor = "default";
			}
		}
	}));

	const onPointerDown = (ev: React.PointerEvent) => {
		if (!props.minimize) return;
		const target = ev.target as HTMLElement;

		if (target.classList.contains("undraggable")) return;

		const undraggableList = document.querySelectorAll(".undraggable");

		let drag = true;

		undraggableList.forEach((undraggable) => {
			if (undraggable.contains(target)) {
				drag = false;
			}
		});

		if (!drag) return;

		listeners.onPointerDown(ev);
	};

	const onPointerUp = (ev: React.PointerEvent) => {
		if (!props.minimize) return;
		listeners.onPointerUp(ev);
	};

	return (
		<div
			ref={divRef}
			css={{
				position: "fixed",
				top: `${coordinate.y}px`,
				left: `${coordinate.x}px`,
				zIndex: ZIndex.FE_WHITEBOARD
			}}
			{...listeners}
			onPointerDown={onPointerDown}
			onPointerUp={onPointerUp}
		>
			{props.children}
		</div>
	);
};

export default forwardRef(MinimizeDraggable) as <T>(
	props: PropsWithChildren<{ minimize: boolean }> & T & { ref?: Ref<DraggableHandle> }
) => React.ReactElement;
